import { client } from 'api/baseApi.ts';
import apiHandler from 'api/handlers/apiHandler.ts';
import ListAssetResponse from 'api/response/ListAssetResponse.ts';
import { costEndpoints } from 'api/rtk-endpoints/CostEndpoints.ts';
import { templateEndpoints } from 'api/rtk-endpoints/TemplateEndpoints.ts';
import { APIConfigData } from 'core/config/APIConfigData';
import { UploadAssetActions } from 'core/constants/enums/UploadAssetActionsEnum.ts';

export const AssetDataIdentifier = {
	ID: 'id',
	URL: 'url',
	STATUS: 'status',
	TAGS: 'tags',
	TYPE: 'type',
	WIDTH: 'width',
	HEIGHT: 'height',
	META: 'metadata',
	EXT: 'ext',
	THUMBNAIL: 'thumbnail',
	IS_NSFW: 'isNsfw',
	NAME: 'name',
};

export const AssetType = {
	IMAGE: 'image',
	VIDEO: 'video',
};

export const formatAssetData = (assetObj) => {
	const {
		asset_id: assetId,
		id,
		metadata,
		tags,
		status,
		is_nsfw: isNsfw,
		type,
		url,
		thumbnail,
		data,
		name,
	} = assetObj;
	const asset = {};
	asset[AssetDataIdentifier.ID] = id ?? assetId;
	asset[AssetDataIdentifier.URL] = url ?? data?.url;
	asset[AssetDataIdentifier.STATUS] = status;
	asset[AssetDataIdentifier.IS_NSFW] = isNsfw;
	asset[AssetDataIdentifier.TAGS] = tags;
	asset[AssetDataIdentifier.TYPE] = type;
	asset[AssetDataIdentifier.META] = metadata;
	asset[AssetDataIdentifier.WIDTH] = metadata?.dim?.width;
	asset[AssetDataIdentifier.HEIGHT] = metadata?.dim?.height;
	asset[AssetDataIdentifier.EXT] = metadata?.ext;
	asset[AssetDataIdentifier.THUMBNAIL] = thumbnail;
	asset[AssetDataIdentifier.NAME] = name ?? data?.name;
	return asset;
};

export const formatPusherAssetData = (assetObj) => {
	const {
		asset_id: assetId,
		id,
		data,
		status,
		is_nsfw: isNsfw,
		type,
		ext,
		thumbnail,
		name,
	} = assetObj;
	const asset = {};
	asset[AssetDataIdentifier.ID] = id ?? assetId;
	asset[AssetDataIdentifier.URL] = data?.url;
	asset[AssetDataIdentifier.STATUS] = status;
	asset[AssetDataIdentifier.IS_NSFW] = isNsfw;
	asset[AssetDataIdentifier.TYPE] = type;
	asset[AssetDataIdentifier.WIDTH] = data?.size?.width;
	asset[AssetDataIdentifier.HEIGHT] = data?.size?.height;
	asset[AssetDataIdentifier.META] = {
		dim: {
			width: data?.size?.width,
			height: data?.size?.height,
		},
	};
	asset[AssetDataIdentifier.EXT] = ext;
	asset[AssetDataIdentifier.THUMBNAIL] = thumbnail;
	asset[AssetDataIdentifier.NAME] = name;
	return asset;
};

export const getAssetsService = async (
	nextTs,
	assetType = AssetType.IMAGE,
	pageSize = APIConfigData.DEFAULT_PAGE_SIZE,
) => {
	const tags = [
		UploadAssetActions.CONTROL_NET,
		UploadAssetActions.FACELOCK,
		UploadAssetActions.CHANGE_IT,
		UploadAssetActions.TRUE_TOUCH,
		UploadAssetActions.FACE_SWAP_IMAGE,
		UploadAssetActions.LIP_SYNC,
		UploadAssetActions.UPSCALE_8K,
		UploadAssetActions.VIDEO_ANYONE,
		UploadAssetActions.FACE_REPLACE,
		UploadAssetActions.FACE_REPLACE_TARGET,
		UploadAssetActions.MUSIC_VIDEO,
		UploadAssetActions.CLOTH_SWAP,
		UploadAssetActions.OBJECT_PLACEMENT,
		UploadAssetActions.IMAGE_REFERENCE_STYLE,
	].join(',');
	const response = await client.assets.list({
		next_ts: nextTs,
		page_size: pageSize,
		type: assetType,
		tags,
	});
	const { data: assetsData, pagination } = response;
	const { has_next: hasNext, next_ts: nextTsValue } = pagination;
	const assets = new ListAssetResponse(assetsData).formatAssetData();
	return { assets, hasNext, assetType, pageSize, nextTs: nextTsValue };
};

export const uploadAssetService = async (
	imageBinary,
	{ width, height, tag, type, size, metadata, ext, mimeType, name },
) => {
	const fixedTag = tag === UploadAssetActions.CHARACTER_STUDIO ? UploadAssetActions.FACELOCK : tag;
	const assetRequest = {
		type,
		name,
		metadata: {
			...(width && height && { dim: { width, height } }),
			size,
			ext,
			...metadata,
		},
		tag: fixedTag,
	};
	const response = await client.assets.upload(imageBinary, mimeType, assetRequest);
	return response.data;
};

export const deleteAsset = async (assetIds, assetType) => {
	await client.assets.archive({ asset_ids: assetIds });
	return { data: assetType };
};

export const getVideoSwapCost = async (assetId, { dispatch }) => {
	return apiHandler({
		endpoint: costEndpoints.endpoints.getVideoSwapCost.initiate,
		args: assetId,
		dispatch,
	});
};

export const formatTemplateData = (item) => ({
	id: item.id,
	url: item.url,
	metadata: {
		name: item.metadata.name,
		coverImageUrl: item.metadata.cover_image_url,
		duration: item.metadata.duration_in_seconds,
		lyrics: item.metadata.lyrics,
		topN: item.metadata.top_n === 'True',
		width: item.metadata.width,
		height: item.metadata.height,
		orientation: item.metadata.orientation,
		destination: item.metadata.destination,
		garmentType: item.metadata.garment_type,
		imageType: item.metadata.image_type,
		highlighted: item.metadata.highlighted,
		thumbnail: item.metadata.thumbnail_url,
		foregroundUrl: item.metadata.foreground_url,
		mediaType: item.metadata.media_type,
		isVideo: item.metadata.is_video,
	},
	displayOrder: item.display_order,
});

export const getTemplatesList = (type, { dispatch }) => {
	return apiHandler({
		endpoint: templateEndpoints.endpoints.list.initiate,
		args: type,
		dispatch,
	});
};
